import { PropsWithChildren, useMemo } from 'react';
import {
  AttributeType,
  RoiSmsData,
  RoiType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { i18next } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { humanizeDateTime } from '@frontend/shared';
import { useSearch } from '@frontend/weave-router';
import { TableColumnConfig } from '@frontend/design-system';
import { MessagingRoiTable, MessagingRoiTableProps } from './messaging-roi-table';

const entryDateConfig: TableColumnConfig<RoiSmsData>[] = [
  {
    accessor: ({ entryDate }) => (entryDate ? humanizeDateTime(entryDate) : '-'),
    Header: i18next.t('Entry Date', { ns: 'analytics' }),
    id: 'entry_date',
    width: 180,
  },
];

const completionDateConfig: TableColumnConfig<RoiSmsData>[] = [
  {
    accessor: ({ apptsCompletionDate }) => (apptsCompletionDate ? humanizeDateTime(apptsCompletionDate) : '-'),
    Header: i18next.t('Appt Completed Date', { ns: 'analytics' }),
    disableSortBy: true,
    id: 'appts_completion_date',
    width: 190,
  },
];

const leadsContactedMiddleColumnConfig: TableColumnConfig<RoiSmsData>[] = [
  {
    accessor: ({ messageDetails }) => messageDetails,
    Header: i18next.t('Message Details', { ns: 'analytics' }),
    disableSortBy: true,
    id: 'message_details',
    width: 300,
  },
  ...entryDateConfig,
  {
    accessor: ({ receiverNumber }) => receiverNumber,
    cellRenderer: (value) => (value ? formatPhoneNumber(value) : '-'),
    Header: i18next.t('Receiver Number', { ns: 'analytics' }),
    id: 'receiver_number',
    width: 180,
  },
];

const patientsConvertedMiddleColumnConfig: TableColumnConfig<RoiSmsData>[] = [
  {
    accessor: ({ practitionerName }) => practitionerName,
    Header: i18next.t('Practitioner Name', { ns: 'analytics' }),
    disableSortBy: true,
    id: 'practitioner_name',
    width: 200,
  },
  {
    accessor: ({ appointmentType }) => appointmentType,
    Header: i18next.t('Appointment Type', { ns: 'analytics' }),
    disableSortBy: true,
    id: 'appointment_type',
    width: 200,
  },
];

export const MessagingSubView = ({
  children,
  roiType,
  ...rest
}: Pick<PropsWithChildren<MessagingRoiTableProps>, 'children' | 'filters' | 'isDemoAccount' | 'roiType'>) => {
  const search = useSearch({ strict: false }) ?? { view: 'leadsContacted' };

  const config = useMemo(() => {
    let attributeType: AttributeType = AttributeType.RAW_SMS;
    let middleColumnConfig: TableColumnConfig<RoiSmsData>[] = [];

    if (roiType === RoiType.MARKETING_MESSAGE) {
      switch (search.view) {
        case 'leadsContacted':
        case 'patientsScheduled':
          attributeType = AttributeType.RAW_SMS;
          middleColumnConfig = leadsContactedMiddleColumnConfig;
          break;

        case 'revenueGenerated':
          attributeType = AttributeType.CONVERTED_PATIENTS;
          middleColumnConfig = [...patientsConvertedMiddleColumnConfig, ...completionDateConfig];
          break;
      }
    } else {
      switch (search.view) {
        case 'leadsContacted':
          attributeType = AttributeType.RAW_SMS;
          middleColumnConfig = leadsContactedMiddleColumnConfig;
          break;

        case 'patientsScheduled':
          attributeType = AttributeType.CONVERTED_PATIENTS;
          middleColumnConfig = [...patientsConvertedMiddleColumnConfig, ...entryDateConfig];
          break;

        case 'revenueGenerated':
          attributeType = AttributeType.CONVERTED_PATIENTS;
          middleColumnConfig = [...patientsConvertedMiddleColumnConfig, ...completionDateConfig];
          break;
      }
    }

    return {
      attributeType,
      middleColumnConfig,
    };
  }, [roiType, search.view]);

  return (
    <>
      {children}
      <MessagingRoiTable
        {...rest}
        attributeType={config.attributeType}
        middleColumnConfig={config.middleColumnConfig}
        roiType={roiType}
        trackingIdBase={search.view}
      />
    </>
  );
};
